/**
 * Aufgabenkreis hinzufügen – eigenständige Tab-Seite
 */
import { useState, useEffect } from 'react';
import { Shield, Check, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { betreuungAPI } from '@/api/betreuungAPI';
import { useTabs } from '@/contexts/TabContext';
import { emitDataRefresh } from '@/lib/tabEvents';
import { toast } from 'sonner';

export default function AufgabenkreisPage() {
  const { activeTab, closeTab } = useTabs();
  const params = activeTab?.params || {};

  const [katalog, setKatalog] = useState([]);
  const [formData, setFormData] = useState({
    key: '', name: '', ist_freitext: false,
    gueltig_ab: '', einschraenkungen: '', beschluss_aktenzeichen: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    betreuungAPI.listAufgabenkreisKatalog().then(k => setKatalog(Array.isArray(k) ? k : [])).catch(() => {});
  }, []);

  const handleKatalogSelect = (key) => {
    const item = katalog.find(k => k.key === key);
    if (item) {
      setFormData(p => ({ ...p, key: item.key, name: item.name, ist_freitext: false }));
    } else if (key === 'freitext') {
      setFormData(p => ({ ...p, key: '', name: '', ist_freitext: true }));
    }
  };

  const handleSave = async () => {
    if (!formData.name) { toast.error('Bitte einen Aufgabenkreis auswählen oder eingeben'); return; }
    setSaving(true);
    try {
      await betreuungAPI.createAufgabenkreis({ klient_id: params.klientId, ...formData, aktiv: true });
      toast.success('Aufgabenkreis hinzugefügt');
      emitDataRefresh('betreuungsakte');
      closeTab(activeTab.id);
    } catch { toast.error('Fehler beim Speichern'); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg mx-auto" data-testid="aufgabenkreis-page">
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Shield className="w-5 h-5 text-purple-600" />Aufgabenkreis hinzufügen
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={() => closeTab(activeTab.id)}><X className="w-4 h-4" /></Button>
          </div>
          <CardDescription>{params.klientName ? `Für ${params.klientName}` : 'Neuen Aufgabenkreis zuweisen'}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Aufgabenkreis</Label>
            <Select onValueChange={handleKatalogSelect}>
              <SelectTrigger><SelectValue placeholder="Auswählen..." /></SelectTrigger>
              <SelectContent className="max-h-60">
                {katalog.map(k => <SelectItem key={k.key} value={k.key}>{k.name}</SelectItem>)}
                <SelectItem value="freitext">Sonstiger (Freitext)...</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {formData.ist_freitext && (
            <div>
              <Label>Bezeichnung (Freitext)</Label>
              <Input value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Aufgabenkreis eingeben..." />
            </div>
          )}
          <div>
            <Label>Gültig ab</Label>
            <Input type="date" value={formData.gueltig_ab} onChange={e => setFormData(p => ({ ...p, gueltig_ab: e.target.value }))} />
          </div>
          <div>
            <Label>Einschränkungen</Label>
            <Input value={formData.einschraenkungen} onChange={e => setFormData(p => ({ ...p, einschraenkungen: e.target.value }))} placeholder="z.B. nur bestimmte Bereiche" />
          </div>
          <div>
            <Label>Beschluss-Aktenzeichen</Label>
            <Input value={formData.beschluss_aktenzeichen} onChange={e => setFormData(p => ({ ...p, beschluss_aktenzeichen: e.target.value }))} placeholder="Az. des Gerichtsbeschlusses" />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => closeTab(activeTab.id)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-aufgabenkreis">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Speichern...</> : <><Check className="w-4 h-4 mr-2" />Hinzufügen</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
